import { useMemo, useState } from "react";
import { styles } from "./rolloutDetails.styles";
import { styles as sharedStyles } from "../../Shared/shared.styles";
import { CommandBar, mergeStyles, Stack, Text } from "@fluentui/react";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@fluentui/react-components";
import RolloutDetailsTable from "./RolloutDetailsTable/RolloutDetailsTable";
import RolloutDetailsForm from "./RolloutDetailsForm/RolloutDetailsForm";
import RollbackDialog from "./RollbackDialog";
import AdvanceDialog from "./AdvanceDialog";
import RolloutDetailsLinks from "./RolloutDetailsLinks/RolloutDetailsLinks";
import { ExperimentType, RolloutActionType } from "../../Types";
import { useFetchPipelineRuns } from "../../Hooks/AdoHooks/UseFetchPipelineRuns";
import { useToast } from "../../Contexts/ToastContext";
import { useFetchPipelineId } from "../../Hooks/AdoHooks/UseFetchPipelineId";
import { ARCHIVE_KEY } from "../../Shared/shared.consts";
import useAppInsights from "../../Hooks/UseAppInsights";
import { useFetchEv2Status } from "../../Hooks/Ev2Hooks/UseFetchEv2Status";
import { useFetchApprovalLinks } from "../../Hooks/AdoHooks/UseFetchPipelineLinks";
import { useDataAccessContext } from "../../Contexts/DataAccessContext";

interface RolloutDetailsProps {
  /**
   * The experiment configuration data for rollouts
   */
  experimentDetails: ExperimentType;
  /**
   * When true, the form is read-only
   */
  isReadOnly?: boolean;
}

/**
 * Comprehensive rollout management interface for experiment deployment and lifecycle.
 * Provides controls for creating new rollouts, advancing stages, and rolling back experiments.
 */
const RolloutDetails: React.FC<RolloutDetailsProps> = ({
  experimentDetails,
  isReadOnly = false,
}) => {
  const { showToast } = useToast();
  const { trackOperationEvent } = useAppInsights("RolloutDetails");
  const { hasAdoAccess } = useDataAccessContext();
  const [isFormPanelOpen, setIsFormPanelOpen] = useState(false);
  const [isRollbackDialogOpen, setIsRollbackDialogOpen] = useState(false);
  const [isAdvanceDialogOpen, setIsAdvanceDialogOpen] = useState(false);
  const [expStudioLink, setExpStudioLink] = useState("");
  const [latestEv2Link, setLatestEv2Link] = useState("");
  const [stagesLoading, setStagesLoading] = useState(false);
  const [accordionIsOpen, setAccordionIsOpen] = useState(true);
  const [error, setError] = useState(null);
  const stageType = "single";
  const { value: pipelineId, loading: pipelineIdLoading } = useFetchPipelineId(
    stageType,
    setError,
  );
  // Remove ARCHIVE_KEY from filename so pipeline runs can still be fetched
  const configFileName = experimentDetails.__filename.replaceAll(
    ARCHIVE_KEY,
    "",
  );

  const {
    value: pipelineRuns = [],
    loading: loadingRuns,
    refresh: refetchPipelineRuns,
  } = useFetchPipelineRuns(pipelineId, configFileName, setError);

  const {
    approvalData,
    loadingApprovals,
    ev2PortalLinks,
    loadingEv2Links,
    refresh: refetchApprovalLinks,
  } = useFetchApprovalLinks(pipelineRuns, setError);

  const {
    ev2StatusMap,
    ev2StatusLoading,
    refresh: refetchEv2Status,
  } = useFetchEv2Status(
    pipelineRuns,
    ev2PortalLinks,
    setError,
    setExpStudioLink,
    setLatestEv2Link,
  );

  const ev2Statuses = useMemo(
    () =>
      Object.values(ev2StatusMap)
        .flat()
        .filter(
          ({ rolloutAction }) =>
            rolloutAction === RolloutActionType.RolloutExperiment,
        ),
    [ev2StatusMap],
  );

  const refreshAll = () => {
    refetchPipelineRuns();
    refetchApprovalLinks();
    refetchEv2Status();
  };

  const onRolloutSubmitted = () => {
    setIsFormPanelOpen(false);
    showToast({
      message: "Experiment rollout started successfully!",
      type: "success",
      dismissible: true,
      duration: 3000,
    });
    refreshAll();
  };

  const onRollbackSubmitted = () => {
    setIsRollbackDialogOpen(false);
    showToast({
      message: "Experiment rollback started successfully!",
      type: "success",
      dismissible: true,
      duration: 3000,
    });
    refreshAll();
  };

  const onAdvanceSubmitted = () => {
    setIsAdvanceDialogOpen(false);
    showToast({
      message: "Advanced to next stage successfully!",
      type: "success",
      dismissible: true,
      duration: 3000,
    });
    refreshAll();
  };

  const experimentInReviewNoRollouts =
    experimentDetails.prUrl && pipelineRuns.length === 0;

  return (
    <Stack
      className={mergeStyles(
        styles.rolloutDetailsRoot,
        !accordionIsOpen && styles.rolloutDetailsAccordionClosed,
      )}
    >
      <Accordion
        defaultOpenItems="rollouts"
        collapsible
        className={sharedStyles.flexOverflowContainer}
        onToggle={() => {
          setAccordionIsOpen((prev) => !prev);
          trackOperationEvent({
            operation: "AccordionToggle",
            function: "Accordion.Rollouts.onToggle",
            properties: { isOpen: !accordionIsOpen },
          });
        }}
      >
        <AccordionItem
          value="rollouts"
          className={mergeStyles(
            sharedStyles.flexOverflowContainer,
            styles.rolloutDetailsAccordionPanel,
          )}
        >
          <AccordionHeader className={styles.rolloutDetailsAccordionHeader}>
            <Text
              variant="large"
              className={styles.rolloutDetailsAccordionHeaderLabel}
            >
              Rollouts
            </Text>
            <RolloutDetailsLinks expLink={expStudioLink} />
          </AccordionHeader>
          <AccordionPanel className={sharedStyles.flexOverflowContainer}>
            <CommandBar
              className={styles.rolloutDetailsCommandBar}
              items={[
                {
                  key: "new",
                  text: "New",
                  iconProps: { iconName: "Add", style: { fontSize: 12 } },
                  onClick: () => {
                    setIsFormPanelOpen(true);
                    trackOperationEvent({
                      operation: "OpenRolloutPanel",
                      function: "CommandBar.New.onClick",
                    });
                  },
                  disabled:
                    !!experimentInReviewNoRollouts ||
                    isReadOnly ||
                    !hasAdoAccess,
                  ariaLabel: "New",
                },
                {
                  key: "advance",
                  text: "Advance",
                  iconProps: { iconName: "Forward", style: { fontSize: 12 } },
                  onClick: () => {
                    setIsAdvanceDialogOpen(true);
                    trackOperationEvent({
                      operation: "OpenAdvanceDialog",
                      function: "CommandBar.Advance.onClick",
                    });
                  },
                  disabled:
                    pipelineRuns.length === 0 || isReadOnly || !hasAdoAccess, // Only enable if there are existing pipeline runs
                  ariaLabel: "Advance",
                },
                {
                  key: "rollback",
                  text: "Rollback",
                  iconProps: { iconName: "Undo", style: { fontSize: 12 } },
                  onClick: () => {
                    setIsRollbackDialogOpen(true);
                    trackOperationEvent({
                      operation: "OpenRollbackDialog",
                      function: "CommandBar.Rollback.onClick",
                    });
                  },
                  disabled:
                    pipelineRuns.length === 0 || isReadOnly || !hasAdoAccess, // Only enable if there are existing pipeline runs
                  ariaLabel: "Rollback",
                },
              ]}
              farItems={[
                {
                  key: "refresh",
                  text: "Refresh",
                  iconProps: { iconName: "Refresh", style: { fontSize: 12 } },
                  onClick: () => {
                    refreshAll();
                    trackOperationEvent({
                      operation: "RefreshRollout",
                      function: "CommandBar.Refresh.onClick",
                    });
                  },
                  disabled: isReadOnly,
                  ariaLabel: "Refresh",
                },
              ]}
            />
            <Stack
              className={mergeStyles(
                sharedStyles.flexOverflowContainer,
                styles.rolloutDetailsPanelContainer,
              )}
            >
              <RolloutDetailsTable
                pipelineId={pipelineId}
                pipelineIdLoading={pipelineIdLoading}
                pipelineRuns={pipelineRuns}
                loadingRuns={loadingRuns}
                approvalData={approvalData}
                loadingApprovals={loadingApprovals}
                ev2PortalLinks={ev2PortalLinks}
                loadingEv2Links={loadingEv2Links}
                ev2StatusMap={ev2StatusMap}
                ev2StatusLoading={ev2StatusLoading}
                runsError={error}
                experimentDetails={experimentDetails}
                setStagesLoading={setStagesLoading}
              />
            </Stack>
            {isFormPanelOpen && (
              <RolloutDetailsForm
                onRolloutSubmitted={onRolloutSubmitted}
                configSpecFilename={configFileName}
                onCancel={() => setIsFormPanelOpen(false)}
              />
            )}
            {isRollbackDialogOpen && (
              <RollbackDialog
                onDismiss={() => setIsRollbackDialogOpen(false)}
                onRollbackSubmitted={onRollbackSubmitted}
                pipelineRuns={pipelineRuns}
                ev2StatusMap={ev2StatusMap}
                configSpecFilename={configFileName}
                isStagesLoading={stagesLoading}
              />
            )}
            {isAdvanceDialogOpen && (
              <AdvanceDialog
                onDismiss={() => setIsAdvanceDialogOpen(false)}
                onAdvanceSubmitted={onAdvanceSubmitted}
                pipelineRuns={pipelineRuns}
                ev2Statuses={ev2Statuses}
                configSpecFilename={configFileName}
                latestEv2PortalLink={latestEv2Link}
                isStagesLoading={stagesLoading}
              />
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Stack>
  );
};

export default RolloutDetails;
